/*
  pdp-buybox.js
  WHAT: PDP buybox behaviour — gallery thumb click + variant change + main image swap +
        price/SKU/ATC update.
  WHY:  Locked WCAG variant picker (fieldset/legend/radio) is server-rendered with the
        selected variant. On change, this script:
          1. Resolves the new variant from the embedded JSON.
          2. Updates the hidden id field that Shopify cart reads.
          3. Updates the price (now / was / save% / ATC label).
          4. Swaps the main image to the variant's featured image when present.
  WHEN: Added 2026-05-28 with PDP buybox build.
*/

(function () {
  'use strict';

  var section = document.querySelector('.ba-pdp-buybox');
  if (!section) return;
  // ─── Add to cart via AJAX so the mini tray opens ────────────────────────
  // [ref: ba-pdp-ajax-add]
  // Owner 2026-09-02: "add to cart on the x5i page goes to cart not to tray."
  //
  // The PDP renders a plain {% form 'product' %}, and nothing intercepted its submit -
  // Impulse only wires AjaxProduct from its own stock product section, which this build
  // replaced. So the browser posted natively to /cart/add and Shopify redirected to the
  // cart page. Confirmed on the dev server: the request was `POST /cart/add` (no `.js`)
  // followed by a navigation.
  //
  // theme.js is loaded with `defer`, so on a fast parse `window.theme` does not exist yet
  // when this file's body runs. Wiring waits for DOMContentLoaded, by which point every
  // deferred script has executed.
  //
  // The sticky footer ATC needs nothing: it already calls form.requestSubmit(), which
  // fires submit listeners, so it inherits this. Its older form.submit() fallback does
  // not - that path still navigates to /cart, which is a safe degradation, not a break.
  // ─── "Add 2 to cart" — buy-2-save-10% secondary CTA ─────────────────────
  // [ref: ba-pdp-add2]
  // Owner 2026-09-02: port the live site's second add-to-cart button.
  //
  // Ported from the live theme's assets/ba-add2save.js, with two deliberate changes:
  //   1. The variant comes from [data-pdp-variant-id], the hidden input this file
  //      already keeps in sync on every variant change. The live script guesses through
  //      a chain of selects and radios because Venture had no single source.
  //   2. On success it opens the mini tray (cart:build + cart:open) instead of calling
  //      Venture's window.get_cart() and falling back to a /cart navigation.
  //
  // It posts quantity 2 and NOTHING about price. The 10% is applied by a Shopify
  // Function once two qualifying units are in the cart, so this must never try to
  // compute a discount itself.
  function wireAdd2() {
    var btn = section.querySelector('[data-ba-add2]');
    if (!btn) return;

    btn.addEventListener('click', function () {
      // Guard against a double-click adding four units.
      if (btn.getAttribute('data-ba-busy') === '1') return;

      var idInput = section.querySelector('[data-pdp-variant-id]');
      var id = idInput && idInput.value;
      if (!id) return;

      btn.setAttribute('data-ba-busy', '1');
      btn.classList.add('is-loading');

      fetch((window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 2 }] })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:build'));
          if (document.getElementById('CartDrawerForm')) {
            document.dispatchEvent(new CustomEvent('cart:open'));
          } else {
            window.location.href = (window.theme && theme.routes && theme.routes.cartPage) || '/cart';
          }
        })
        .catch(function () {
          // Never leave a dead-looking button: the shopper can retry.
          btn.classList.add('has-error');
        })
        .then(function () {
          btn.removeAttribute('data-ba-busy');
          btn.classList.remove('is-loading');
        });
    });
  }
  wireAdd2();

  function wirePdpAjaxAdd() {
    var form = section.querySelector('[data-pdp-form]');
    if (!form || !form.querySelector('[data-pdp-atc]')) return;
    if (!window.theme || !theme.AjaxProduct || !theme.routes || !theme.routes.cartAdd) return;
    new theme.AjaxProduct(form, '[data-pdp-atc]');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wirePdpAjaxAdd);
  } else {
    wirePdpAjaxAdd();
  }


  var jsonNode = section.querySelector('[data-pdp-product-json]');
  if (!jsonNode) return;

  var productData;
  try {
    productData = JSON.parse(jsonNode.textContent);
  } catch (e) {
    if (window.console && console.warn) console.warn('[pdp-buybox] failed to parse product JSON:', e);
    return;
  }

  var moneyFormat = (window.Shopify && window.Shopify.money_format) || '${{amount}}';

  // ─── Money helpers ───────────────────────────────────────────────────────
  /*
    Counterpart to snippets/ba-price.liquid — see the note in pdp-sticky-atc.js.
    window.Shopify.money_format is `AUD ${{amount}}` on this shop when it is defined
    at all, so the code is stripped here to keep JS-written prices identical to the
    Liquid-rendered ones rather than dependent on whether that global exists.
  */
  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, dollars)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
      .replace(/\b[A-Z]{3}\b\s*/g, '')
      .trim();
  }

  // ─── Gallery thumb click ────────────────────────────────────────────────
  // Owner 2026-06-18: gallery now renders product.media (image / video /
  // external_video). Each media item is a stacked container; only the active
  // one is visible. Thumb click finds the media item with the matching
  // data-media-id and activates it. Any videos that were playing in
  // previously-active items get paused.
  section.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-pdp-thumb]');
    if (!thumb) return;
    e.preventDefault();

    var targetId = thumb.getAttribute('data-media-id');
    if (!targetId) return;

    setActiveMedia(targetId);

    section.querySelectorAll('[data-pdp-thumb]').forEach(function (t) {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    thumb.classList.add('is-active');
    thumb.setAttribute('aria-selected', 'true');
  });

  function setActiveMedia(mediaId) {
    var items = section.querySelectorAll('[data-pdp-media-item]');
    items.forEach(function (item) {
      var match = item.getAttribute('data-media-id') === String(mediaId);
      if (match) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
        // Pause any video / iframe that just got hidden so playback doesn't
        // continue off-screen.
        var vid = item.querySelector('video');
        if (vid && !vid.paused) {
          try { vid.pause(); } catch (err) { /* ignore */ }
        }
      }
    });
  }

  // ─── Variant change ──────────────────────────────────────────────────────
  function getSelectedOptions() {
    var fieldsets = section.querySelectorAll('[data-pdp-variant-fieldset]');
    var selected = [];
    fieldsets.forEach(function (fs) {
      var checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) selected.push(checked.value);
    });
    return selected;
  }

  function findVariant(selectedValues) {
    if (!productData || !productData.variants) return null;
    return productData.variants.find(function (v) {
      var opts = [v.option1, v.option2, v.option3].filter(function (x) { return x !== null && x !== undefined; });
      if (opts.length !== selectedValues.length) return false;
      return opts.every(function (o, i) { return o === selectedValues[i]; });
    });
  }

  function updateForVariant(variant) {
    if (!variant) return;

    // Hidden variant id (the one Shopify reads on cart add).
    var hidden = section.querySelector('[data-pdp-variant-id]');
    if (hidden) hidden.value = variant.id;

    // URL — keep the variant query param in sync.
    if (window.history && window.history.replaceState) {
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* noop */ }
    }

    // Price block.
    var nowEl = section.querySelector('[data-pdp-price]');
    var wasEl = section.querySelector('[data-pdp-was]');
    var saveEl = section.querySelector('[data-pdp-save]');
    var atcPriceEl = section.querySelector('[data-pdp-atc-price]');

    if (nowEl) nowEl.textContent = formatMoney(variant.price);
    if (atcPriceEl) atcPriceEl.textContent = formatMoney(variant.price);

    // Sale-badge overlay on the main image (top-left). Mirrors the info-column
    // save% with the same value, kept in sync on variant change.
    var galleryBadge = section.querySelector('[data-pdp-gallery-save]');
    var galleryBadgePct = section.querySelector('[data-pdp-gallery-save-pct]');

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (wasEl) {
        wasEl.innerHTML = '<s>' + formatMoney(variant.compare_at_price) + '</s>';
        wasEl.hidden = false;
      }
      // Dollar saving, matching the Liquid (owner 2026-08-26). Writing a percentage
      // here would put it back on the first variant change.
      var saving = formatMoney(variant.compare_at_price - variant.price);
      if (saveEl) {
        saveEl.textContent = 'Save ' + saving;
        saveEl.hidden = false;
      }
      // Hero overlay stays a PERCENTAGE (owner 2026-08-26) while the buybox and the
      // sticky bar state dollars - the chip is small and sits over artwork.
      if (galleryBadgePct) {
        galleryBadgePct.textContent = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
      }
      if (galleryBadge) galleryBadge.hidden = false;
      var priceWrap = section.querySelector('.ba-pdp-info__price');
      if (priceWrap) priceWrap.classList.add('is-on-sale');
    } else {
      if (wasEl) wasEl.hidden = true;
      if (saveEl) saveEl.hidden = true;
      if (galleryBadge) galleryBadge.hidden = true;
      var priceWrap2 = section.querySelector('.ba-pdp-info__price');
      if (priceWrap2) priceWrap2.classList.remove('is-on-sale');
    }

    // ATC button availability.
    var atc = section.querySelector('[data-pdp-atc]');
    var atcLabel = section.querySelector('[data-pdp-atc-label]');
    if (atc) {
      atc.disabled = !variant.available;
      if (atcLabel) {
        if (variant.available) {
          atcLabel.innerHTML = 'Add to cart · <span data-pdp-atc-price>' + formatMoney(variant.price) + '</span>';
        } else {
          atcLabel.textContent = 'Sold out';
        }
      }
    }

    // Selected-value mirror in each fieldset legend.
    var fieldsets = section.querySelectorAll('[data-pdp-variant-fieldset]');
    fieldsets.forEach(function (fs) {
      var idx = parseInt(fs.getAttribute('data-option-index'), 10);
      var sel = fs.querySelector('input[type="radio"]:checked');
      var label = fs.querySelector('[data-pdp-variant-selected-label]');
      if (sel && label) label.textContent = sel.value;
      // Active class on the label wrapping the checked input.
      fs.querySelectorAll('.ba-pdp-info__variant-opt').forEach(function (lbl) {
        var input = lbl.querySelector('input[type="radio"]');
        if (input && input.checked) {
          lbl.classList.add('is-active');
        } else {
          lbl.classList.remove('is-active');
        }
      });
    });

    // Activate the variant's featured media if present. Works for any media
    // type (image / video / external_video / model) because the gallery now
    // renders all media items stacked, keyed by media id.
    var featuredMediaId = null;
    if (variant.featured_media && variant.featured_media.id) {
      featuredMediaId = variant.featured_media.id;
    } else if (variant.featured_image && variant.featured_image.id) {
      featuredMediaId = variant.featured_image.id;
    }
    if (featuredMediaId) {
      setActiveMedia(featuredMediaId);

      // Activate the matching thumb.
      var matchingThumb = section.querySelector('[data-pdp-thumb][data-media-id="' + featuredMediaId + '"]');
      if (matchingThumb) {
        section.querySelectorAll('[data-pdp-thumb]').forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        matchingThumb.classList.add('is-active');
        matchingThumb.setAttribute('aria-selected', 'true');
      }
    }
  }

  section.addEventListener('change', function (e) {
    var input = e.target.closest('[data-pdp-variant-input]');
    if (!input) return;
    var selected = getSelectedOptions();
    var variant = findVariant(selected);
    updateForVariant(variant);
  });

  // Note: previous syncThumbsHeight() (matched mock 08:1309-1323 vertical
  // thumb column to main image height) was removed 2026-05-29 when the
  // gallery moved to main-image-on-top + horizontal thumb strip below
  // (im8health pattern, user request). Height sync no longer applicable.
})();
